class SizeChartModal extends HTMLElement {
  constructor() {
    super()
    this.modal = document.querySelector('.SizeChartModal')
    this.closeButton = this.modal.querySelector('.SizeChartModal__close')
    this.overlay = this.modal.querySelector('.SizeChartModal__overlay')
    this.modalBody = this.modal.querySelector('.SizeChartModal__body')
    this.findSizeButton = this.querySelector('.js-open-size-chart-modal')
    this.lastFocusedElement = null
    this.isModalOpen = false
    this.handleOpenModal = this.handleOpenModal.bind(this)
    this.handleCloseModal = this.handleCloseModal.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleFocusIn = this.handleFocusIn.bind(this)
  }

  static FOCUSABLE_SELECTOR = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
  static HEADER_GROUP_SELECTOR = '.shopify-section.shopify-section-group-header-group'

  setHeaderGroupZIndex() {
    document.querySelectorAll(SizeChartModal.HEADER_GROUP_SELECTOR).forEach(el => {
      el.style.setProperty('z-index', '10', 'important')
    })
  }

  resetHeaderGroupZIndex() {
    document.querySelectorAll(SizeChartModal.HEADER_GROUP_SELECTOR).forEach(el => {
      el.style.removeProperty('z-index')
    })
  }

  getFocusableElements() {
    if (!this.modal) return []
    return Array.from(this.modal.querySelectorAll(SizeChartModal.FOCUSABLE_SELECTOR))
      .filter(el => el.offsetParent !== null && !el.hasAttribute('hidden'))
  }

  connectedCallback() {
    if (this.findSizeButton) {
      this.findSizeButton.addEventListener('click', this.handleOpenModal)
    }
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseModal)
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleCloseModal)
    }
  }

  disconnectedCallback() {
    if (this.findSizeButton) {
      this.findSizeButton.removeEventListener('click', this.handleOpenModal)
    }
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseModal)
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleCloseModal)
    }
    this.removeFocusTrap()
  }

  addFocusTrap() {
    document.addEventListener('keydown', this.handleKeyDown, true)
    document.addEventListener('focusin', this.handleFocusIn, true)
  }

  removeFocusTrap() {
    document.removeEventListener('keydown', this.handleKeyDown, true)
    document.removeEventListener('focusin', this.handleFocusIn, true)
  }

  async handleOpenModal() {
    if (!this.modal || this.isModalOpen) {
      return
    }

    this.isModalOpen = true
    this.lastFocusedElement = document.activeElement

    try {
      this.modalBody.innerHTML = '<p>Loading...</p>'
      const response = await fetch('/pages/size-chart')
      if (!response.ok) throw new Error('Failed to load size chart')

      const html = await response.text()
      const mainElement = new DOMParser().parseFromString(html, 'text/html').querySelector('#MainContent')
      this.modalBody.innerHTML = mainElement.innerHTML
    } catch (error) {
      console.error('Error loading size chart:', error)
      this.modalBody.innerHTML = '<p>Error loading size chart. Please try again later.</p>'
    }

    this.modal.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
    this.setHeaderGroupZIndex()
    this.addFocusTrap()

    const focusableElements = this.getFocusableElements()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    } else {
      this.closeButton?.focus()
    }
  }

  handleCloseModal() {
    if (!this.modal || this.modal.classList.contains('hidden')) {
      return
    }

    this.modal.classList.add('hidden')
    document.body.style.overflow = ''
    this.resetHeaderGroupZIndex()
    this.removeFocusTrap()
    this.isModalOpen = false

    if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
      requestAnimationFrame(() => {
        this.lastFocusedElement.focus()
      })
    }
  }

  handleFocusIn(event) {
    if (!this.isModalOpen || !this.modal) return
    if (this.modal.contains(event.target)) return

    const focusable = this.getFocusableElements()
    if (focusable.length > 0) {
      setTimeout(() => focusable[0].focus(), 0)
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.handleCloseModal()
      return
    }
    if (event.key !== 'Tab') return

    const focusable = this.getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const current = document.activeElement
    const currentIndex = focusable.indexOf(current)
    const isInsideModal = this.modal.contains(current)

    if (event.shiftKey) {
      if (current === first || !isInsideModal || currentIndex === -1) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (current === last || !isInsideModal || currentIndex === -1) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}

customElements.define('size-chart-modal', SizeChartModal)
